<template>
  <div class="strategy-editor">
    <div class="top-bar">
      <div class="toolbar">
        <input v-model="strategyName" placeholder="Strategy Name" class="name-input"/>
        <button @click="saveStrategy" class="btn btn-primary">Save</button>
        <button @click="runBacktest" class="btn btn-success">Run Backtest</button>
        <button @click="startPaperTrading" class="btn btn-warning">Start Paper Trading</button>
      </div>
      <div class="config">
          <label>Start:</label> <input v-model="startDate" type="date">
          <label>End:</label> <input v-model="endDate" type="date">
          <label>Capital:</label> <input v-model="initialCapital" type="number">
          <label>Freq:</label> 
          <select v-model="frequency">
              <option value="day">Day</option>
              <option value="minute">Minute</option>
              <option value="3s">3s</option>
          </select>
      </div>
    </div>

    <div class="main-content">
        <div class="left-panel">
             <textarea v-model="code" class="code-editor" spellcheck="false" placeholder="Enter your strategy code here..."></textarea>
        </div>
        <div class="right-panel">
            <div class="metrics-panel" v-if="result && result.metrics">
                <div class="metric-item">
                    <span class="metric-label">Total Return</span>
                    <span class="metric-value" :class="getColor(result.metrics.total_return)">{{ result.metrics.total_return }}%</span>
                </div>
                 <div class="metric-item">
                    <span class="metric-label">Annual Return</span>
                    <span class="metric-value" :class="getColor(result.metrics.annual_return)">{{ result.metrics.annual_return }}%</span>
                </div>
                 <div class="metric-item">
                    <span class="metric-label">Max Drawdown</span>
                    <span class="metric-value green">{{ result.metrics.max_drawdown }}%</span>
                </div>
                 <div class="metric-item">
                    <span class="metric-label">Sharpe Ratio</span>
                    <span class="metric-value">{{ result.metrics.sharpe_ratio }}</span>
                </div>
                 <div class="metric-item">
                    <span class="metric-label">Volatility</span>
                    <span class="metric-value">{{ result.metrics.volatility }}%</span>
                </div>
            </div>
            
            <div class="chart-panel">
                <div class="chart-container" ref="chart"></div>
            </div>
            
            <div class="logs-panel">
                <div class="logs-header">Logs</div>
                <div class="logs-content" ref="logsContainer">
                    <div v-for="(log, index) in logs" :key="index" class="log-line" :class="getLogClass(log)">
                        {{ log }}
                    </div>
                    <div v-if="logs.length === 0" class="no-logs">No logs available</div>
                </div>
            </div>
        </div>
    </div>
  </div>
</template>

<script>
import { request_local } from 'network/request' 
import echarts from 'echarts'

export default {
  name: 'StrategyEditor',
  data() {
    return {
      strategyId: null,
      strategyName: 'My Strategy',
      code: `# Simple Strategy Example
def initialize(context):
    # Set the stock to trade
    context.security = '600519'

def handle_data(context, data):
    # Get current price (mocked)
    price = data.current(context.security, 'close_px')
    
    # Simple logic: buy if not held
    current_position = context.portfolio.positions.get(context.security, 0)
    
    if current_position == 0:
        order(context.security, 100)
        log.info("Buying 100 shares")
`,
      startDate: '2023-01-01',
      endDate: '2023-12-31',
      initialCapital: 100000,
      frequency: 'day',
      result: null,
      logs: [],
      chartInstance: null,
      theme: 'light'
    }
  },
  mounted() {
    try{ const t = localStorage.getItem('site.theme'); if(t){ this.theme = t } }catch(e){ console.warn('theme read failed', e) }
    this.onThemeChanged = (e)=>{ 
       const theme = e && e.detail && e.detail.theme ? e.detail.theme : (localStorage.getItem('site.theme')||'light'); 
       this.theme = theme; 
       if(this.chartInstance) {
          this.chartInstance.dispose();
          this.chartInstance = null;
          this.drawChart();
       }
    }
    window.addEventListener('site-theme-changed', this.onThemeChanged)
  },
  beforeDestroy() {
    window.removeEventListener('site-theme-changed', this.onThemeChanged)
  },
  methods: {
    saveStrategy() {
      request_local({
        url: '/quant/backtest/strategy/save',
        method: 'post',
        data: {
            id: this.strategyId,
            name: this.strategyName,
            code: this.code
        }
      }).then(res => {
          if(res.data.code === 20000) {
              this.strategyId = res.data.data.id;
              alert('Saved successfully!');
          } else {
              alert('Save failed: ' + res.data.message);
          }
      }).catch(err => {
          alert('Error: ' + err.message);
      })
    },
    runBacktest() {
        this.result = null;
        this.logs = [];
        request_local({
            url: '/quant/backtest/backtest/run',
            method: 'post',
            data: {
                strategy_id: this.strategyId,
                code: this.code,
                start_date: this.startDate,
                end_date: this.endDate,
                initial_capital: this.initialCapital,
                frequency: this.frequency
            }
        }).then(res => {
            if(res.data.code === 20000) {
                this.result = res.data.data;
                this.logs = this.result.logs || [];
                this.$nextTick(() => {
                    this.drawChart();
                    this.scrollToBottom();
                });
            } else {
                alert('Backtest failed: ' + res.data.message);
            }
        }).catch(err => {
             alert('Error: ' + err.message);
        })
    },
    drawChart() {
        if (!this.result || !this.result.equity_curve) return;
        
        const dates = this.result.equity_curve.map(item => item.date);
        const equities = this.result.equity_curve.map(item => item.equity);
        const benchmark = equities.map(() => this.initialCapital); // Placeholder for benchmark
        
        const textColor = this.theme === 'dark' ? '#999999' : '#333333';
        const axisLineColor = this.theme === 'dark' ? '#666666' : '#ccc';

        const option = {
            backgroundColor: this.theme === 'dark' ? 'transparent' : 'transparent',
            title: {
                text: 'Equity Curve',
                left: 'center',
                textStyle: { color: textColor }
            },
            tooltip: {
                trigger: 'axis'
            },
            legend: {
                data: ['Strategy', 'Benchmark'],
                top: 30,
                textStyle: { color: textColor }
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '3%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: dates,
                axisLabel: { color: textColor },
                axisLine: { lineStyle: { color: axisLineColor } }
            },
            yAxis: {
                type: 'value',
                scale: true,
                axisLabel: { color: textColor },
                axisLine: { lineStyle: { color: axisLineColor } },
                splitLine: { show: false }
            },
            series: [{
                name: 'Strategy',
                data: equities,
                type: 'line',
                smooth: true,
                itemStyle: { color: '#e74c3c' }
            }, {
                 name: 'Benchmark',
                 data: benchmark,
                 type: 'line',
                 smooth: true,
                 itemStyle: { color: '#95a5a6' },
                 lineStyle: { type: 'dashed' }
            }]
        };
        
        if (!this.chartInstance) {
            this.chartInstance = echarts.init(this.$refs.chart, this.theme === 'dark' ? 'site-dark' : undefined);
        }
        this.chartInstance.setOption(option);
        this.chartInstance.resize();
    },
    startPaperTrading() {
        if (!this.strategyId) {
            alert("Please save strategy first.");
            return;
        }
        const accountId = prompt("Enter Account ID for Paper Trading:", "1");
        if (!accountId) return;

        request_local({
            url: '/quant/paper/start',
            method: 'post',
            data: {
                strategy_id: this.strategyId,
                account_id: accountId
            }
        }).then(res => {
            if(res.data.code === 20000) {
                alert('Paper Trading Started! ID: ' + res.data.data.id);
            } else {
                alert('Failed to start: ' + res.data.message);
            }
        }).catch(err => {
             alert('Error: ' + err.message);
        })
    },
    getColor(val) {
        if (val > 0) return 'red';
        if (val < 0) return 'green';
        return '';
    },
    getLogClass(log) {
        if (log.startsWith('ERROR')) return 'log-error';
        if (log.startsWith('WARNING')) return 'log-warning';
        return 'log-info';
    },
    scrollToBottom() {
        const container = this.$refs.logsContainer;
        if (container) {
            container.scrollTop = container.scrollHeight;
        }
    }
  }
}
</script>

<style scoped>
.strategy-editor {
    display: flex;
    flex-direction: column;
    height: 100vh;
    overflow: hidden;
    padding: 10px;
    box-sizing: border-box;
    background-color: var(--bg-main);
}

.top-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 10px;
    border-bottom: 1px solid var(--border-color);
    flex-shrink: 0;
}

.toolbar {
    display: flex;
    align-items: center;
}

.name-input {
    padding: 5px;
    margin-right: 10px;
    width: 200px;
    border: 1px solid var(--border-color);
    background-color: var(--bg-card);
    color: var(--text-main);
    border-radius: 4px;
}

.btn {
    margin-right: 5px;
    padding: 5px 15px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    color: white;
}

.btn-primary { background-color: var(--accent-color); }
.btn-success { background-color: #2ecc71; }
.btn-warning { background-color: #f1c40f; color: #333; }

.config {
    display: flex;
    align-items: center;
    background: var(--bg-muted);
    padding: 5px 10px;
    border-radius: 4px;
}

.config label {
    margin-left: 15px;
    margin-right: 5px;
    font-weight: bold;
    font-size: 14px;
    color: var(--text-main);
}
.config label:first-child { margin-left: 0; }
.config input, .config select {
    padding: 3px;
    border: 1px solid var(--border-color);
    background-color: var(--bg-card);
    color: var(--text-main);
    border-radius: 3px;
}

.main-content {
    display: flex;
    flex: 1;
    margin-top: 10px;
    overflow: hidden; /* Prevent outer scroll */
}

.left-panel {
    flex: 1;
    margin-right: 10px;
    display: flex;
    flex-direction: column;
    border: 1px solid var(--border-color);
}

.code-editor {
    flex: 1;
    width: 100%;
    resize: none;
    padding: 10px;
    border: none;
    font-family: 'Consolas', 'Monaco', monospace;
    font-size: 14px;
    line-height: 1.5;
    background-color: #2c3e50; /* Always dark for code editors usually */
    color: #ecf0f1;
    outline: none;
}

.right-panel {
    flex: 1;
    display: flex;
    flex-direction: column;
    border: 1px solid var(--border-color);
}

.metrics-panel {
    display: flex;
    justify-content: space-around;
    padding: 15px 10px;
    background-color: var(--bg-card);
    border-bottom: 1px solid var(--border-color);
    flex-shrink: 0;
}

.metric-item {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.metric-label {
    font-size: 12px;
    color: var(--text-muted);
    margin-bottom: 5px;
}

.metric-value {
    font-size: 18px;
    font-weight: bold;
    color: var(--text-main);
}

.red { color: #e74c3c; }
.green { color: #27ae60; }

.chart-panel {
    flex: 1; /* Takes remaining space */
    min-height: 200px;
    padding: 10px;
    background-color: var(--bg-card);
    position: relative;
}

.chart-container {
    width: 100%;
    height: 100%;
}

.logs-panel {
    height: 30%; /* Fixed percentage height for logs */
    border-top: 1px solid var(--border-color);
    display: flex;
    flex-direction: column;
    background-color: #000;
}

.logs-header {
    padding: 5px 10px;
    background-color: var(--bg-muted);
    font-weight: bold;
    font-size: 12px;
    color: var(--text-main);
    border-bottom: 1px solid var(--border-color);
}

.logs-content {
    flex: 1;
    overflow-y: auto;
    padding: 10px;
    font-family: 'Consolas', 'Monaco', monospace;
    font-size: 12px;
}

.log-line {
    margin-bottom: 2px;
    white-space: pre-wrap;
    word-break: break-all;
}

.log-info { color: #bdc3c7; }
.log-error { color: #e74c3c; }
.log-warning { color: #f39c12; }

.no-logs {
    color: #7f8c8d;
    font-style: italic;
}
</style>
